"use client";
import { useCallback, useEffect, useRef, useState } from "react";

import BrandedFooter from "../../../footer/branded";
import { TemplateStyleProvider } from "../template-style-context";

import { useAppDispatch, useAppSelector } from "../../../slice/hooks";
import { getActiveCart, getAllCartItemsCount } from "../../../slice/cart-slice";
import { useHistoryManager } from "../../../history/useHistoryManager";
import { setActiveCategory, setCategories } from "../../../slice/category-slice";
import {
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../../../slice/accessOrg-slice";
import { getCategoryUrlPath } from "../../../../lib/util";

const getCartItemCount = (cart) => {
  const paymentItem = Array.isArray(cart?.paymentItem) ? cart.paymentItem : [];
  return paymentItem.reduce((total, item) => total + Number(item?.unit || 0), 0);
};

const getStoreUrlData = ({
  organisation,
  accessPointId,
  accessPoint,
  accessPoints,
}) => ({
  organisation,
  accessPointId,
  accessPoint,
  accessPoints,
});

const getMarketplaceUrlData = () => ({
  accessPoint: null,
  accessPoints: [],
  accessPointId: null,
  organisation: null,
});

const resetMarketplaceContext = (dispatch, resetCategories = false) => {
  dispatch(setAccessPointId(null));
  dispatch(setAccessPoint(null));
  dispatch(setAccessPoints([]));
  dispatch(setOrganisation(null));

  if (resetCategories) {
    dispatch(setActiveCategory(null));
    dispatch(setCategories(null));
  }
};

const buildSearchPath = (storePath, term) => {
  const editedTerm = term.trim().replace(/\s+/g, "-");
  return `${storePath}?q=${editedTerm}`;
};

const buildAuthPath = (storePath, navLocation) => {
  if (navLocation !== "marketplace") return "auth";
  const cleanStorePath = String(storePath || "").replace(/\/+$/, "");
  return `${cleanStorePath}/auth`;
};

const MarketplaceStoresBanner = ({ show, navigateTo, dispatch }) => {
  if (!show) return null;

  const goToMarketplaceStores = () => {
    navigateTo(
      "market-place/stores",
      {
        urlData: getMarketplaceUrlData(),
        onNavigate: (navigate) => {
          if (navigate) {
            resetMarketplaceContext(dispatch, true);
          }
        },
      },
      false,
    );
  };

  return (
    <div
      style={{
        backgroundColor: "#0e86d4",
        color: "white",
        padding: "1rem",
        cursor: "pointer",
      }}
      onClick={goToMarketplaceStores}
    >
      <h2>Visit Store in our MarketPlace </h2>
    </div>
  );
};

const StickyHeaderSpacer = ({ show, height }) => {
  if (!show) return null;
  return <div style={{ height }} />;
};

const SearchOverlay = ({ show, onClose, styles }) => {
  if (!show) return null;
  return <div className={styles.searchOverlay} onClick={onClose} />;
};

const StoreBoxDrawer = ({
  show,
  Drawermenu,
  StoreSideMenu,
  setShowMenu,
  organisation,
  accessPoints,
  accessPointId,
  categories,
  activeCategory,
  activeSubCategory,
  switchCategory,
  switchSubCategory,
  storeBoxLocationChange,
}) => {
  if (!show) return null;

  return (
    <Drawermenu setShowMenu={setShowMenu} showMenu={show}>
      {({ closeMenu }) => (
        <StoreSideMenu
          organisation={organisation}
          accessPoints={accessPoints}
          accessPointId={accessPointId}
          categories={categories}
          activeCategory={activeCategory}
          activeSubCategory={activeSubCategory}
          onClose={closeMenu}
          onSwitchCategory={(e) => {
            closeMenu();
            switchCategory?.(e);
          }}
          onSwitchSubCategory={(e) => {
            closeMenu();
            switchSubCategory?.(e);
          }}
          onLocationChange={(e) => {
            storeBoxLocationChange(e);
            closeMenu();
          }}
        />
      )}
    </Drawermenu>
  );
};

function StoreBox({
  styles,
  templateClassNames,
  Drawermenu,
  StoreSideMenu,
  StoreHeader,
  ChatReachOutBox,
  includeCategoryNavigationInHeader = false,
  organisation,
  children,
  modal,
  showCart,
  accessPointId,
  handleLocationChange,
  storePath,
  categories,
  switchCategory,
  switchSubCategory,
  activeCategory,
  activeSubCategory,
  searchTerm,
  setSearchTerm,
  isPageMode = false,
  headerClassName = "",
  bodyClassName = "",
  showSearchBar = true,
  forceHeaderSearch = false,
  isHomePage = false,
  searchBarRef = null,
  hasTyped = false,
  setHasTyped,
  accessPoint,
  accessPoints,
  navLocation,
  urlPath,
  isSwitching = false,
  showMarketDiv = false,
  inMainMarketplace = false,
}) {
  // UI state

  const [isSticky, setIsSticky] = useState(false); // tracks if header should be sticky
  const [showMenu, setShowMenu] = useState(false); // drawer menu toggle
  const [searchFocus, setSearchFocus] = useState(false); // expanded search on mobile

  const [pastHomeSearch, setPastHomeSearch] = useState(false); // track scroll past homepage search

  // Redux state

  const cart = useAppSelector(getActiveCart);
  const allCartItemsCount = useAppSelector(getAllCartItemsCount);

  // refs
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const searchInputRef = useRef(null);

  const { navigateTo } = useHistoryManager(storePath);
  const dispatch = useAppDispatch();

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 750);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const cartCount =
    navLocation === "marketplace" ? allCartItemsCount : getCartItemCount(cart);

  // --- Search URL builder ---
  const urlWithSearchQuery = (term) => {
    if (typeof window !== "undefined") {
      navigateTo(buildSearchPath(storePath, term), { override: true }, false);
    }
  };

  const storeBoxLocationChange = (e) => {
    handleLocationChange(e);
  };

  const getStoreNavigationData = useCallback(
    () =>
      getStoreUrlData({
        organisation,
        accessPointId,
        accessPoint,
        accessPoints,
      }),
    [organisation, accessPointId, accessPoint, accessPoints],
  );

  const resolvedSwitchCategory = useCallback(
    (categoryId) => {
      if (typeof switchCategory === "function") {
        switchCategory(categoryId);
        return;
      }

      dispatch(setActiveCategory(categoryId || ""));
      setSearchTerm?.("");
      const navigationData = getStoreNavigationData();
      const category = categories?.find(
        (item) => item?.productCategoryId === categoryId,
      );

      if (category?.name) {
        navigateTo(getCategoryUrlPath(storePath, category.name), {
          urlData: navigationData,
          onNavigate: (navigate) => {
            if (navigate) {
              dispatch(setActiveCategory(categoryId));
            }
          },
        }, false);
        return;
      }

      navigateTo("home", {
        urlData: navigationData,
      });
    },
    [
      categories,
      dispatch,
      getStoreNavigationData,
      navigateTo,
      setSearchTerm,
      storePath,
      switchCategory,
    ],
  );

  const resolvedSwitchSubCategory = useCallback(
    (subCategory) => {
      if (typeof switchSubCategory === "function") {
        switchSubCategory(subCategory);
        return;
      }

      dispatch(setActiveCategory(subCategory?.categoryId || ""));
      setSearchTerm?.("");
      const navigationData = getStoreNavigationData();
      const category = categories?.find(
        (item) => item?.productCategoryId === subCategory?.categoryId,
      );

      if (category?.name) {
        navigateTo(getCategoryUrlPath(storePath, category.name), {
          urlData: navigationData,
          onNavigate: (navigate) => {
            if (navigate) {
              dispatch(setActiveCategory(subCategory?.categoryId || ""));
            }
          },
        }, false);
        return;
      }

      navigateTo("home", { urlData: navigationData });
    },
    [
      categories,
      dispatch,
      getStoreNavigationData,
      navigateTo,
      setSearchTerm,
      storePath,
      switchSubCategory,
    ],
  );

  const handleLogoClick = () => {
    setSearchTerm("");
    dispatch(setActiveCategory(null));
    const urldata = getStoreUrlData({
      organisation,
      accessPointId,
      accessPoint,
      accessPoints,
    });

    if (showSearchBar) {
      if (navLocation === "marketplace") {
        navigateTo(
          "market-place",
          {
            urlData: getMarketplaceUrlData(),
            onNavigate: (navigate) => {
              if (navigate) {
                resetMarketplaceContext(dispatch, true);
              }
            },
          },
          false,
        );
      } else
        navigateTo("home", {
          urlData: urldata,
        });
    } else setShowMenu(true);
  };

  const handleLoginClick = () => {
    navigateTo(buildAuthPath(storePath, navLocation));
  };
  const onLogout = () => {};

  const onSeeAllSearchResults = () => {
    navigateTo(
      "market-place",
      {
        urlData: getMarketplaceUrlData(),
        onNavigate: (navigate) => {
          if (navigate) {
            resetMarketplaceContext(dispatch);
          }
        },
      },
      false,
    );
  };

  // --- Auto-focus search input when expanded ---
  useEffect(() => {
    if (searchFocus && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchFocus]);

  // --- Track header height for spacer when sticky ---
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    let frame = null;
    const updateHeaderHeight = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setHeaderHeight(header.offsetHeight);
      });
    };

    updateHeaderHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeaderHeight);
      return () => {
        if (frame) cancelAnimationFrame(frame);
        window.removeEventListener("resize", updateHeaderHeight);
      };
    }

    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(header);
    window.addEventListener("resize", updateHeaderHeight);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, [
    showSearchBar,
    searchFocus,
    searchTerm,
    hasTyped,
    isSticky,
    pastHomeSearch,
    categories,
  ]);

  // --- Sticky header logic (scroll or body lock) ---
  useEffect(() => {
    const handleScrollOrLock = () => {
      const bodyFixed = document.body.style.position === "fixed";
      if (bodyFixed) {
        setIsSticky(true);
      } else {
        setIsSticky(window.scrollY > 0);
      }
    };

    handleScrollOrLock(); // run on mount

    window.addEventListener("scroll", handleScrollOrLock);

    // Also track body style changes (when search modal locks body)
    const observer = new MutationObserver(() => handleScrollOrLock());
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => {
      window.removeEventListener("scroll", handleScrollOrLock);
      observer.disconnect();
    };
  }, []);

  // --- Handle search input changes ---
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm?.(value);
  };

  const cancelSearch = (e) => {
    e.preventDefault();
    setSearchTerm("");
    setSearchFocus(false);
    setHasTyped?.(false);
  };

  // --- Track if user scrolled past the home page search bar ---
  useEffect(() => {
    if (!isHomePage || !searchBarRef?.current) return;

    const handleScroll = () => {
      if (!searchBarRef?.current) return;
      const rect = searchBarRef.current.getBoundingClientRect();
      const scrolledPast = rect.bottom <= headerHeight; // compare position
      setPastHomeSearch(scrolledPast);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage, searchBarRef, headerHeight]);

  const [isAtTop, setIsAtTop] = useState(true);

  useEffect(() => {
    const onScroll = () => {
      setIsAtTop(window.scrollY === 0);
    };

    window.addEventListener("scroll", onScroll);
    onScroll(); // initial check

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Use debounced search to reduce flicker in UI
  const hasSearch = searchTerm !== "";

  const categoryHeaderProps = includeCategoryNavigationInHeader
    ? {
        categories,
        activeCategory,
        activeSubCategory,
        switchCategory: resolvedSwitchCategory,
        switchSubCategory: resolvedSwitchSubCategory,
      }
    : {};

  return (
    <TemplateStyleProvider value={templateClassNames}>
      <div className={`${styles.page} ${bodyClassName}`}>
        <MarketplaceStoresBanner
          show={navLocation === "marketplace" && showMarketDiv}
          navigateTo={navigateTo}
          dispatch={dispatch}
        />

        <StoreHeader
          headerRef={headerRef}
          headerClassName={headerClassName}
          showSearchBar={showSearchBar}
          isSticky={isSticky}
          isAtTop={isAtTop}
          searchFocus={searchFocus}
          storePath={storePath}
          hasSearch={hasSearch}
          handleLogoClick={handleLogoClick}
          isSwitching={isSwitching}
          organisation={organisation}
          accessPoints={accessPoints}
          accessPointId={accessPointId}
          hasTyped={hasTyped}
          isHomePage={isHomePage}
          pastHomeSearch={pastHomeSearch}
          forceHeaderSearch={forceHeaderSearch}
          searchTerm={searchTerm}
          searchInputRef={searchInputRef}
          handleSearch={handleSearch}
          cancelSearch={cancelSearch}
          setSearchFocus={setSearchFocus}
          setHasTyped={setHasTyped}
          urlWithSearchQuery={urlWithSearchQuery}
          navLocation={navLocation}
          onSeeAllSearchResults={onSeeAllSearchResults}
          modal={modal}
          storeBoxLocationChange={storeBoxLocationChange}
          isPageMode={isPageMode}
          showCart={showCart}
          cartCount={cartCount}
          onLogout={onLogout}
          handleLoginClick={handleLoginClick}
          urlPath={urlPath}
          {...categoryHeaderProps}
        />

        <StickyHeaderSpacer
          show={showSearchBar || isSticky}
          height={headerHeight}
        />

        <SearchOverlay
          show={searchFocus && !isHomePage}
          onClose={() => setSearchFocus(false)}
          styles={styles}
        />

        {/* Page content */}
        {children}

        {/* Footer */}
        <div className={`${styles.footer} box-footer`}>
          <BrandedFooter
            organisation={organisation}
            storePath={storePath}
            isSwitching={isSwitching}
          />
        </div>
      </div>
      {storePath !== "market-place" && (
        <ChatReachOutBox
          modal={modal}
          organisation={organisation}
          accessPoint={accessPoint}
        />
      )}

      <StoreBoxDrawer
        show={showMenu && !showSearchBar && isMobile}
        Drawermenu={Drawermenu}
        StoreSideMenu={StoreSideMenu}
        setShowMenu={setShowMenu}
        organisation={organisation}
        accessPoints={accessPoints}
        accessPointId={accessPointId}
        categories={categories}
        activeCategory={activeCategory}
        activeSubCategory={activeSubCategory}
        switchCategory={resolvedSwitchCategory}
        switchSubCategory={resolvedSwitchSubCategory}
        storeBoxLocationChange={storeBoxLocationChange}
      />
    </TemplateStyleProvider>
  );
}

export default StoreBox;
